import React from "react";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { useTranslation } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import "./App.css";
import "./i18n/i18n";
import ScrollToTop from './components/ScrollToTop';

// Context Providers
import { AuthProvider } from './contexts/AuthContext';

// Components
import Header from './components/Header';
import Footer from './components/Footer';
import CookieConsent from './components/CookieConsent';
import GoogleAnalytics from './components/GoogleAnalytics';
import TrackingScripts from './components/TrackingScripts';
import ProtectedRoute from './components/admin/ProtectedRoute';
import AdminLayout from './components/admin/AdminLayout';

// Public Pages
import Home from './pages/Home';
import Portfolio from './pages/Portfolio';
import About from './pages/About';
import Services from './pages/Services';
import Reviews from './pages/Reviews';
import Blog from './pages/Blog';
import BlogPost from './pages/BlogPost';
import Contact from './pages/Contact';
import Booking from './pages/Booking';
import PriceCalculator from './pages/PriceCalculator';
import AGB from './pages/AGB';
import Datenschutz from './pages/Datenschutz';
import Impressum from './pages/Impressum';
import NotFound from './pages/NotFound';

// Dynamic City Page
import DynamicCityPage from './pages/DynamicCityPage';
import DynamicRouteHandler from './components/DynamicRouteHandler';

// Admin Pages
import AdminLogin from './pages/admin/AdminLogin';
import Dashboard from './pages/admin/Dashboard';
import AdminPortfolio from './pages/admin/AdminPortfolio';
import AdminBlog from './pages/admin/AdminBlog';
import AdminForms from './pages/admin/AdminForms';
import AdminReviews from './pages/admin/AdminReviews';
import AdminSettings from './pages/admin/AdminSettings';
import AdminLocations from './pages/admin/AdminLocations';
import AdminTracking from './pages/admin/AdminTracking';
import AdminBeforeAfter from './pages/admin/AdminBeforeAfter';
import AdminCalculator from './pages/admin/AdminCalculator';
import Admin2FA from './pages/admin/Admin2FA';

function App() {
  const { i18n } = useTranslation();

  return (
    <HelmetProvider>
      <AuthProvider>
        <div className="App bg-black text-white min-h-screen">
          <GoogleAnalytics />
          <TrackingScripts />
          <BrowserRouter>
            <ScrollToTop />
          <Routes>
            {/* Admin Routes */}
            <Route path="/admin/login" element={<AdminLogin />} />
            <Route path="/admin/*" element={
              <ProtectedRoute>
                <AdminLayout />
              </ProtectedRoute>
            }>
              <Route path="dashboard" element={<Dashboard />} />
              <Route path="portfolio" element={<AdminPortfolio />} />
              <Route path="blog" element={<AdminBlog />} />
              <Route path="reviews" element={<AdminReviews />} />
              <Route path="forms" element={<AdminForms />} />
              <Route path="locations" element={<AdminLocations />} />
              <Route path="tracking" element={<AdminTracking />} />
              <Route path="before-after" element={<AdminBeforeAfter />} />
              <Route path="calculator" element={<AdminCalculator />} />
              <Route path="2fa" element={<Admin2FA />} />
              <Route path="settings" element={<AdminSettings />} />
              <Route index element={<Dashboard />} />
            </Route>

            {/* Public Routes */}
            <Route path="/*" element={
              <>
                <Header />
                <main>
                  <Routes>
                    <Route path="/" element={<Home />} />
                    <Route path="/portfolio" element={<Portfolio />} />
                    <Route path="/about" element={<About />} />
                    <Route path="/services" element={<Services />} />
                    <Route path="/reviews" element={<Reviews />} />
                    <Route path="/blog" element={<Blog />} />
                    <Route path="/blog/:slug" element={<BlogPost />} />
                    <Route path="/contact" element={<Contact />} />
                    <Route path="/kontakt" element={<Contact />} />
                    <Route path="/booking" element={<Booking />} />
                    <Route path="/buchung" element={<Booking />} />
                    <Route path="/calculator" element={<PriceCalculator />} />
                    <Route path="/preiskalkulator" element={<PriceCalculator />} />
                    <Route path="/leistungen" element={<Services />} />
                    <Route path="/uber-mich" element={<About />} />
                    <Route path="/bewertungen" element={<Reviews />} />
                    <Route path="/agb" element={<AGB />} />
                    <Route path="/datenschutz" element={<Datenschutz />} />
                    <Route path="/impressum" element={<Impressum />} />
                    
                    {/* City Landing Pages - Dynamic Routing */}
                    <Route path="/city/:slug" element={<DynamicCityPage />} />
                    
                    {/* Legacy static routes - redirect to dynamic routes */}
                    <Route path="/gera" element={<DynamicCityPage slug="gera" />} />
                    <Route path="/ronneburg" element={<DynamicCityPage slug="ronneburg" />} />
                    <Route path="/chemnitz" element={<DynamicCityPage slug="chemnitz" />} />
                    <Route path="/erfurt" element={<DynamicCityPage slug="erfurt" />} />
                    <Route path="/leipzig" element={<DynamicCityPage slug="leipzig" />} />
                    <Route path="/jena" element={<DynamicCityPage slug="jena" />} />
                    <Route path="/zwickau" element={<DynamicCityPage slug="zwickau" />} />
                    
                    {/* 404 Not Found Page */}
                    <Route path="/404" element={<NotFound />} />
                    
                    {/* Catch-all for dynamic city routes created through admin */}
                    <Route path="/*" element={<DynamicRouteHandler />} />
                  </Routes>
                </main>
                <Footer />
                <CookieConsent />
              </>
            } />
          </Routes>
        </BrowserRouter>
      </div>
    </AuthProvider>
  </HelmetProvider>
  );
}

export default App;